import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MapPin, Ruler, Wallet, ArrowUpRight, Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const StatusBadge = ({ status }) => {
  const open = status === 'Open';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-display text-[11px] font-bold uppercase tracking-wider ${open ? 'bg-farmway-gold text-farmway-green' : 'bg-white/10 text-farmway-cream/80'}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${open ? 'bg-farmway-green' : 'bg-farmway-leaf'}`} />
      {status}
    </span>
  );
};

export default function ProjectCard({ project, onOpen }) {
  const { user, isSaved, toggleSaved } = useAuth();
  const navigate = useNavigate();
  const saved = isSaved(project.id);

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!user) {
      toast.info('Sign in to save projects');
      navigate('/login');
      return;
    }
    const res = await toggleSaved(project.id);
    if (res.ok) toast.success(res.saved ? 'Saved to your list' : 'Removed from saved');
  };

  const open = () => onOpen(project);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && open()}
      className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/10 bg-farmway-green-mid/40 text-left outline-none transition-all duration-500 hover:-translate-y-1.5 hover:border-farmway-leaf/40 hover:shadow-[0_24px_60px_-24px_rgba(0,0,0,0.7)] focus-visible:ring-2 focus-visible:ring-farmway-gold"
    >
      <div className="relative h-48 overflow-hidden">
        <img src={project.image} alt={project.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-farmway-green-dark/90 via-farmway-green-dark/10 to-transparent" />
        <div className="absolute left-4 top-4">
          <StatusBadge status={project.status} />
        </div>
        <button
          onClick={handleSave}
          aria-label={saved ? 'Remove from saved' : 'Save project'}
          className={`absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full backdrop-blur-md transition-all ${
            saved ? 'bg-farmway-gold text-farmway-green' : 'bg-black/30 text-farmway-cream hover:bg-black/50'
          }`}
        >
          <Heart className={`h-4 w-4 ${saved ? 'fill-farmway-green' : ''}`} />
        </button>
        <div className="absolute bottom-3 left-4 flex items-center gap-1.5 text-farmway-cream">
          <MapPin className="h-3.5 w-3.5 text-farmway-gold" />
          <span className="text-xs font-medium">{project.location}, {project.country}</span>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-lg font-bold text-farmway-cream">{project.name}</h3>
          <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-farmway-cream/40 transition-all duration-300 group-hover:text-farmway-gold" />
        </div>
        <p className="mt-1 text-sm text-farmway-leaf-bright">{project.crop}</p>

        {project.metrics && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-white/5 px-3 py-2">
              <p className="font-display text-base font-bold text-farmway-gold">{project.metrics.irr}</p>
              <p className="text-[10px] uppercase tracking-wider text-farmway-cream/50">Target Net IRR</p>
            </div>
            <div className="rounded-lg bg-white/5 px-3 py-2">
              <p className="font-display text-base font-bold text-farmway-cream">{project.metrics.multiple}</p>
              <p className="text-[10px] uppercase tracking-wider text-farmway-cream/50">Equity Multiple</p>
            </div>
          </div>
        )}

        <div className="mt-auto grid grid-cols-2 gap-4 border-t border-white/10 pt-4 text-farmway-cream/80">
          <div className="flex items-center gap-2">
            <Ruler className="h-4 w-4 text-farmway-leaf" />
            <span className="text-sm">{project.size}</span>
          </div>
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-farmway-leaf" />
            <span className="text-sm">{project.projectSize}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
